/**
 * The text of the Aṣṭādhyāyī's sūtras, for labelling derivation steps.
 *
 * A bare rule number tells a reader nothing. The sūtra's own words are short and
 * *are* the rule — 8.2.7 is `nalopaḥ prātipadikāntasya`, "elision of the n at the
 * end of a prātipadika", which is precisely the step that turns *ātman* into
 * *ātmā*. So the text is shown beside the number, and the number links out to a
 * full commentary.
 *
 * The map is ~145 KB, so it is fetched only when a derivation is actually
 * opened, not with the declension table.
 */

let cache: Record<string, string> | null = null;
let pending: Promise<Record<string, string>> | null = null;

/** Loads the sūtra map, once. */
export const loadSutras = (): Promise<Record<string, string>> => {
    if (cache) return Promise.resolve(cache);
    if (pending) return pending;

    pending = import("./data/sutras.json")
        .then(m => {
            cache = (m.default ?? m) as Record<string, string>;
            return cache;
        })
        .catch(() => {
            // Losing the labels is not worth breaking the derivation view over.
            cache = {};
            return cache;
        });

    return pending;
};

/**
 * A derivation step cites more than the Aṣṭādhyāyī. A code like `01.0934` is a
 * Dhātupāṭha entry, not a sūtra — it is how the engine records "this root was
 * read from the root-list" — so it has no sūtra text and gets a plain label
 * rather than an empty cell.
 */
const DHATUPATHA_REF = /^\d{2}\.\d{4}$/;

/** The label for a rule code: its sūtra text, or what kind of citation it is. */
export const sutraText = (code: string): string | undefined => {
    const text = cache?.[code];
    if (text) return text;
    if (DHATUPATHA_REF.test(code)) return "Dhātupāṭha";
    return undefined;
};

/**
 * A link to the sūtra on ashtadhyayi.com, which carries the Kāśikā and other
 * commentaries. Rule codes are dotted (`1.2.45`); the site wants them as path
 * segments.
 *
 * Vārttikas and other non-sūtra rules are cited by the engine with codes that do
 * not resolve there, so those are left unlinked.
 */
export const sutraUrl = (code: string): string | undefined => {
    const parts = code.split(".");
    if (parts.length !== 3) return undefined;
    if (!parts.every(p => /^\d+$/.test(p))) return undefined;
    return `https://ashtadhyayi.com/sutraani/${parts[0]}/${parts[1]}/${parts[2]}`;
};
